import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddEditCallAlertModal, type AddEditCallAlertSaveData } from '@/components/modals/AddEditCallAlertModal';
import { EditCallAlertTypesModal } from '@/components/modals/EditCallAlertTypesModal';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue } from '@/utils/legacy-utils';
import moment from 'moment-timezone';
import { AccessAuthorities } from '@/models';

import { getAvailableSubmodules, getCurrentCompanyId } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
import { DATETIME_FORMAT_SERVER_UTC, DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME } from '@/constants/formats';
import { validateAddressWithSuggestions } from '@/utils/custom-location';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const fillInAddressFromSuggestions = _wg('fillInAddressFromSuggestions');
const indexOfValueInArrayByProperty = _wg('indexOfValueInArrayByProperty');
const getSubmoduleOrKerktonRoleFromLogEntry = _wg('getSubmoduleOrKerktonRoleFromLogEntry');

type CallAlertTypes = Array<{ id: number; type: string; backgroundColor: string; borderColor: string; textColor: string }>;

function isCallAlertManagementReadOnly(): boolean {
  let submodules: string[] | null = getAvailableSubmodules();
  if (submodules === null) {
    submodules = [];
  }
  return !isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT);
}

const callAlertColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'Id', size: 'hidden' },
  { field: 'callAlertTypeId', name: 'Call Alert Type Id', size: 'hidden' },
  {
    field: 'locFormattedAddress',
    name: 'Location',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.locFormattedAddress)) {
        return e.locFormattedAddress;
      }
      return 'Error getting location.';
    },
  },
  {
    field: 'callAlertTypeType',
    name: 'Type',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.callAlertTypeType)) {
        return e.callAlertTypeType;
      }
      return '';
    },
  },
  {
    field: 'message',
    name: 'Message',
    size: 'col-xs-4',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.message)) {
        const maxMessageLength = 50;
        let message = e.message;
        if (message.length > maxMessageLength) {
          message = message.substring(0, maxMessageLength - 3) + '...';
        }
        return message;
      }
      return 'Error getting message.';
    },
  },
  {
    field: 'alertStartTime',
    name: 'Start Time',
    size: 'col-xs-1',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.alertStartTime)) {
        return moment(e.alertStartTime).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME);
      }
      return '';
    },
  },
  {
    field: 'alertEndTime',
    name: 'End Time',
    size: 'col-xs-1',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.alertEndTime)) {
        return moment(e.alertEndTime).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME);
      }
      return '';
    },
  },
];

const auditLogColumns: KstDataGridColumn[] = [
  {
    field: 'dateCreated',
    name: 'Date / Time',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.dateCreated)) {
        return moment(e.dateCreated).format('MM/DD/YYYY HH:mm:ss');
      }
      return 'Error getting date / time.';
    },
  },
  {
    field: 'loggedByFirstName',
    name: 'First',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.loggedByFirstName) ? e.loggedByFirstName : ''),
  },
  {
    field: 'loggedByLastName',
    name: 'Last',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.loggedByLastName) ? e.loggedByLastName : ''),
  },
  {
    field: 'role',
    name: 'Role',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && hasValue(e.role) ? getSubmoduleOrKerktonRoleFromLogEntry(e) : ''),
  },
  {
    field: 'action',
    name: 'Action',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && hasValue(e.action) ? e.action : ''),
  },
  {
    field: 'entry',
    name: 'Entry',
    size: 'col-xs-4',
    template: (e: any) => (hasValue(e) && hasValue(e.entry) ? e.entry : ''),
  },
];

export function CallAlertManagementScreen() {
  const { theme } = useTheme();

  const isReadOnly = useMemo(() => isCallAlertManagementReadOnly(), []);

  const [activeTab, setActiveTab] = useState('showManageCallAlertTab');
  const [activePage, setActivePage] = useState('showManageCallAlert');

  // Call alert entries
  const [callAlertEntries, setCallAlertEntries] = useState<any[] | null>(null);
  const [alertTypes, setAlertTypes] = useState<CallAlertTypes | null>(null);

  // Loading states
  const [isCallAlertsLoading, setIsCallAlertsLoading] = useState<boolean>(false);
  const [isAuditLogLoading, setIsAuditLogLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Audit log entries
  const [auditLogEntries, setAuditLogEntries] = useState<any[]>([]);
  const [auditLogPager, setAuditLogPager] = useState<KstDataGridPager>({
    curPage: undefined,
    numPages: undefined,
    records: undefined,
    isVisible: false,
  });

  // Caches
  const callAlertListCacheRef = useRef<any | null>(null);
  const callAlertTypeListCacheRef = useRef<any | null>(null);
  const auditLogPageRef = useRef<number | null>(null);
  const auditLogListCacheRef = useRef<any | null>(null);

  // Add/Edit modal
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [addEditMode, setAddEditMode] = useState<'add' | 'edit'>('add');
  const [addEditCallAlert, setAddEditCallAlert] = useState<any>({});

  // Edit Types modal
  const [editTypesModalOpen, setEditTypesModalOpen] = useState(false);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Grid ref
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const callAlertRows: KstDataGridRows = useMemo(() => ({ entries: callAlertEntries }), [callAlertEntries]);
  const auditLogRows: KstDataGridRows = useMemo(() => ({ entries: auditLogEntries }), [auditLogEntries]);

  const headerButtons = useMemo(
    () =>
      isReadOnly
        ? []
        : [
            { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
            { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
            { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
          ],
    [isReadOnly]
  );

  const mobileTabs = useMemo(() => {
    const t = [{ id: 'showManageCallAlertTab', label: 'Call Alerts' }];
    if (!isReadOnly) {
      t.push({ id: 'showManageCallAlertTypesTab', label: 'Alert Types' });
      t.push({ id: 'showCallAlertAuditLogTab', label: 'Audit Log' });
    }
    return t;
  }, [isReadOnly]);

  // Cache helpers
  const getCallAlertList = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (hasValue(callAlertListCacheRef.current)) {
        resolve(deepCopyProperties(callAlertListCacheRef.current));
      } else {
        api.listCallAlerts(
          (result: any) => {
            callAlertListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting call alerts.', 'error');
          }
        );
      }
    });
  }, []);

  const getCallAlertTypeList = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (hasValue(callAlertTypeListCacheRef.current)) {
        resolve(deepCopyProperties(callAlertTypeListCacheRef.current));
      } else {
        api.listCallAlertTypes(
          (result: any) => {
            callAlertTypeListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting call alert types.', 'error');
          }
        );
      }
    });
  }, []);

  const clearCaches = useCallback(() => {
    callAlertListCacheRef.current = null;
    callAlertTypeListCacheRef.current = null;
  }, []);

  const clearAuditLogCaches = useCallback(() => {
    auditLogPageRef.current = null;
    auditLogListCacheRef.current = null;
  }, []);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    }, 0);
  }, []);

  const refreshManageCallAlertList = useCallback(
    (doScrollToBottom = false) => {
      setIsCallAlertsLoading(true);
      Promise.all([getCallAlertList(), getCallAlertTypeList()]).then((values) => {
        const listResponse: any = values[0];
        const alertTypesResponse: { alertTypes: CallAlertTypes } = values[1];

        // Sort by listIdx
        listResponse.alerts.sort((a: any, b: any) => a.listIdx - b.listIdx);
        // Renumber
        for (let i = 0; i < listResponse.alerts.length; i++) {
          listResponse.alerts[i].listIdx = i + 1;
          listResponse.alerts[i].isListEnabled = true;
        }

        // Map type names
        const typeIdToType: Record<number, string> = alertTypesResponse.alertTypes.reduce(
          (acc: any, { id, type }: any) => {
            acc[id] = type;
            return acc;
          },
          {}
        );
        for (const entry of listResponse.alerts) {
          entry.callAlertTypeType = typeIdToType[entry.callAlertTypeId];
        }

        setCallAlertEntries(listResponse.alerts);
        setIsCallAlertsLoading(false);

        // Update alert types
        const sorted = [...alertTypesResponse.alertTypes].sort((a, b) => a.id - b.id);
        setAlertTypes(sorted);

        if (doScrollToBottom) {
          scrollToBottom();
        }
      }).catch(() => {
        setIsCallAlertsLoading(false);
      });
    },
    [getCallAlertList, getCallAlertTypeList, scrollToBottom]
  );

  const getAuditLogPage = useCallback(
    (page: number) => {
      const cached =
        hasValue(auditLogPageRef.current) &&
        hasValue(auditLogListCacheRef.current) &&
        auditLogPageRef.current === page;

      const doFetch = (response: any) => {
        const entries: any[] = [];
        for (let i = 0; i < response.logs.length; i++) {
          const entry = response.logs[i];
          entry.isChecked = false;
          entries.push(entry);
        }
        setAuditLogEntries(entries);
        setAuditLogPager({
          curPage: response.curPage,
          numPages: response.numPages,
          records: response.records,
          isVisible: true,
        });
        setIsAuditLogLoading(false);
      };

      setIsAuditLogLoading(true);
      if (cached) {
        doFetch(deepCopyProperties(auditLogListCacheRef.current));
      } else {
        api.getCallAlertAuditLogEntries(
          { data: { page, pageSize: 30 } },
          (result: any) => {
            auditLogPageRef.current = page;
            auditLogListCacheRef.current = result;
            doFetch(deepCopyProperties(result));
          },
          () => {
            App.showPopupNotification('Error getting call alert audit log.', 'error');
            setIsAuditLogLoading(false);
          }
        );
      }
    },
    []
  );

  // Load initial data
  useEffect(() => {
    refreshManageCallAlertList();
  }, []);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'showManageCallAlertTab') {
        setActivePage('showManageCallAlert');
        refreshManageCallAlertList();
      } else if (tabId === 'showManageCallAlertTypesTab') {
        setActivePage('showManageCallAlertTypes');
        getCallAlertTypeList().then((response: { alertTypes: CallAlertTypes }) => {
          const sorted = [...response.alertTypes].sort((a, b) => a.id - b.id);
          setAlertTypes(sorted);
        });
      } else if (tabId === 'showCallAlertAuditLogTab') {
        setActivePage('showCallAlertAuditLog');
        getAuditLogPage(1);
      }
    },
    [refreshManageCallAlertList, getCallAlertTypeList, getAuditLogPage]
  );

  const handleButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'add') {
        setAddEditMode('add');
        setAddEditCallAlert({});
        setAddEditModalOpen(true);
      } else if (functionName === 'edit') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Please select the call alert to edit by clicking the checkbox.', 'warning');
          return;
        } else if (checkedItems.length > 1) {
          App.showPopupNotification('Only one call alert may be edited at a time.', 'warning');
          return;
        }
        const item = checkedItems[0];
        setAddEditMode('edit');
        setAddEditCallAlert({
          id: item.id,
          locFormattedAddress: item.locFormattedAddress,
          callAlertTypeId: item.callAlertTypeId,
          message: item.message,
          alertStartTime: item.alertStartTime,
          alertEndTime: item.alertEndTime,
        });
        setAddEditModalOpen(true);
      } else if (functionName === 'delete') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Call Alert must be selected to delete.', 'warning');
          return;
        }
        const count = checkedItems.length;
        const message =
          count === 1
            ? 'Are you sure you want to delete this call alert?'
            : `Are you sure you want to delete ${count} call alerts?`;
        setDeleteConfirmItems(checkedItems);
        setDeleteConfirmMessage(message);
        setDeleteConfirmOpen(true);
      }
    },
    []
  );

  const handleAddEditSave = useCallback(
    (data: AddEditCallAlertSaveData) => {
      const callAlertData: any = {
        locFormattedAddress: data.locFormattedAddress,
        callAlertTypeId: data.callAlertTypeId,
        message: data.message,
        alertStartTime: data.alertStartTime
          ? moment.tz(moment(data.alertStartTime).format(), 'UTC').format(DATETIME_FORMAT_SERVER_UTC)
          : null,
        alertEndTime: data.alertEndTime
          ? moment.tz(moment(data.alertEndTime).format(), 'UTC').format(DATETIME_FORMAT_SERVER_UTC)
          : null,
      };

      const doSaveOrUpdate = () => {
        setIsSaving(true);
        if (addEditMode === 'add') {
          api.saveCallAlert(
            callAlertData,
            () => {
              setIsSaving(false);
              clearCaches();
              clearAuditLogCaches();
              refreshManageCallAlertList(true);
              setAddEditModalOpen(false);
            },
            () => {
              setIsSaving(false);
              App.showPopupNotification('Failed to save call alert.', 'error');
            }
          );
        } else {
          callAlertData.id = data.id;
          api.updateCallAlert(
            callAlertData,
            () => {
              setIsSaving(false);
              clearCaches();
              clearAuditLogCaches();
              refreshManageCallAlertList(true);
              setAddEditModalOpen(false);
            },
            () => {
              setIsSaving(false);
              App.showPopupNotification('Failed to update call alert.', 'error');
            }
          );
        }
      };

      if (data.locationChanged) {
        setIsSaving(true);
        validateAddressWithSuggestions({
          address: data.locFormattedAddress,
          errorMessage: (window as any).addressValidationErrorMsg,
          isLocationRequired: true,
          companyId: getCurrentCompanyId(),
          // Previously-saved coords for discrepancy detection on edits.
          previousLat: (callAlertData as any)?.latitude ?? null,
          previousLng: (callAlertData as any)?.longitude ?? null,
        })
          .then((locationResultsObj: any) => {
            fillInAddressFromSuggestions(locationResultsObj, callAlertData, null, (window as any).DataObjectLocationFields.callAlert);
            doSaveOrUpdate();
          })
          .catch(() => {
            setIsSaving(false);
          });
      } else {
        doSaveOrUpdate();
      }
    },
    [addEditMode, clearCaches, clearAuditLogCaches, refreshManageCallAlertList]
  );

  const handleUpdateTypeList = useCallback(() => {
    if (!alertTypes) return;
    const typeList = alertTypes.map((elt) => ({ callAlertTypeId: elt.id, type: elt.type }));
    api.updateCallAlertTypes(
      { callAlertTypes: typeList },
      () => {
        clearCaches();
        App.showPopupNotification('Updated call alert types.', 'success');
      },
      () => {
        App.showPopupNotification('Failed to update call alert types.', 'error');
      }
    );
  }, [alertTypes, clearCaches]);

  const handleConfirmDelete = useCallback(() => {
    const idList = deleteConfirmItems.map((elt: any) => elt.id);
    setIsDeleting(true);
    api.deleteCallAlerts(
      { idList },
      () => {
        setIsDeleting(false);
        clearCaches();
        clearAuditLogCaches();
        refreshManageCallAlertList(true);
        setDeleteConfirmOpen(false);
        setDeleteConfirmItems([]);
        setDeleteConfirmMessage('');
      },
      () => {
        setIsDeleting(false);
        App.showPopupNotification('Failed to delete call alerts.', 'error');
      }
    );
  }, [deleteConfirmItems, clearCaches, clearAuditLogCaches, refreshManageCallAlertList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowMoved = useCallback(
    (row: any) => {
      setCallAlertEntries((prev) => {
        const values = [...(prev ?? [])];
        const idx = values.findIndex((v: any) => v.id === row.id);
        if (idx >= 0) {
          values.splice(idx, 1);
        }
        values.splice(row.listIdx - 1, 0, row);
        for (let i = 0; i < values.length; i++) {
          values[i].listIdx = i + 1;
        }

        // Save reorder
        const idList = values.map((v: any) => v.id);
        api.reorderCallAlerts(
          { idList },
          () => {
            clearCaches();
            clearAuditLogCaches();
          },
          () => {
            App.showPopupNotification('Failed to reorder call alerts.', 'error');
          }
        );

        return values;
      });
    },
    [clearCaches, clearAuditLogCaches]
  );

  const handleAlertTypeChange = useCallback((index: number, newType: string) => {
    setAlertTypes((prev) => {
      if (!prev) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], type: newType };
      return updated;
    });
  }, []);

  return (
    <div className={theme}>
      {/* Modals */}
      <AddEditCallAlertModal
        open={addEditModalOpen}
        mode={addEditMode}
        callAlert={addEditCallAlert}
        alertTypes={alertTypes || []}
        theme={theme}
        saving={isSaving}
        onSave={handleAddEditSave}
        onCancel={() => setAddEditModalOpen(false)}
      />

      <EditCallAlertTypesModal
        open={editTypesModalOpen}
        alertTypes={alertTypes || []}
        theme={theme}
        onSave={(data) => {
          api.updateCallAlertTypes(
            data,
            () => {
              clearCaches();
              App.showPopupNotification('Updated call alert types.', 'success');
              setEditTypesModalOpen(false);
            },
            () => {
              App.showPopupNotification('Failed to update call alert types.', 'error');
            }
          );
        }}
        onCancel={() => setEditTypesModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Call Alert"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={mobileTabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showManageCallAlertTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showManageCallAlertTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Call Alerts</span>
              </li>
              {!isReadOnly && (
                <li
                  className={`kst-sidebar-item${activeTab === 'showManageCallAlertTypesTab' ? ' kst-sidebar-item--active' : ''}`}
                  onClick={() => handleTabChange('showManageCallAlertTypesTab')}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="4" y1="21" x2="4" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="4" y1="10" x2="4" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="12" y1="21" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="12" y1="8" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="20" y1="21" x2="20" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="20" y1="12" x2="20" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="1" y1="14" x2="7" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="9" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="17" y1="16" x2="23" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span className="kst-sidebar-text">Alert Types</span>
                </li>
              )}
              {!isReadOnly && (
                <li
                  className={`kst-sidebar-item${activeTab === 'showCallAlertAuditLogTab' ? ' kst-sidebar-item--active' : ''}`}
                  onClick={() => handleTabChange('showCallAlertAuditLogTab')}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="kst-sidebar-text">Audit Log</span>
                </li>
              )}
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Call Alert Management Page */}
            {activePage === 'showManageCallAlert' && (
              <div className="kst-page" ref={gridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Call Alert Management</h3>
                  <p className="kst-page-description">
                    Add alerts that show at the top of Calls for Service when a Call has been sent or updated with that location.
                  </p>
                </div>

                <KstDataGrid
                  columns={callAlertColumns}
                  rows={callAlertRows}
                  headerbuttons={headerButtons}
                  showcheckbox
                  showordercolumn
                  mobiletitlefield="locFormattedAddress"
                  loading={isCallAlertsLoading}
                  onButtonCommand={handleButtonCommand}
                  onRowMoved={handleRowMoved}
                />
              </div>
            )}

            {/* Call Alert Types Page */}
            {activePage === 'showManageCallAlertTypes' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Call Alert Types</h3>
                  <p className="kst-page-description">
                    Update the names of the Call Alert types. These are shown in the Call Alerts grid to make it easier to identify which Call Alerts correspond to which colors.
                  </p>
                </div>

                <div className="kst-alert-types-container">
                  {alertTypes &&
                    alertTypes.map((alertType, index) => (
                      <div key={alertType.id} className="kst-alert-type-item">
                        <div
                          className="kst-alert-type-preview"
                          style={{
                            color: '#' + alertType.textColor,
                            backgroundColor: '#' + alertType.backgroundColor,
                            borderColor: '#' + alertType.borderColor,
                          }}
                        >
                          Call Alert Content
                        </div>
                        <input
                          type="text"
                          className="kstc-input"
                          value={alertType.type}
                          onChange={(e) => handleAlertTypeChange(index, e.target.value)}
                        />
                      </div>
                    ))}
                  <div className="kst-alert-types-actions">
                    <button type="button" className="kstc-btn kstc-btn--primary" onClick={handleUpdateTypeList}>
                      Save Types
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Audit Log Page */}
            {activePage === 'showCallAlertAuditLog' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Call Alert Audit Log</h3>
                </div>

                <KstDataGrid
                  columns={auditLogColumns}
                  rows={auditLogRows}
                  pager={auditLogPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="entry"
                  loading={isAuditLogLoading}
                  onGoToPage={getAuditLogPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CallAlertManagementScreen;

